import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CardRow from '@/domains/dashboard-management/components/CardRow';
import CardTotal from '@/domains/dashboard-management/components/CardTotal/CardTotal';
import {
  useAdminProductSummary,
  useAdminProducts,
} from '@/domains/dashboard-management/hooks/useAdminProducts';
import {
  PRODUCT_STATUS_BADGE,
  PRODUCT_STATUS_OPTIONS,
} from '@/domains/dashboard-management/utils/productStatus';
import { STATUS_COURIER } from '@/domains/mobile-web/constant/listStatusCourier.constant';
import { formatPrice } from '@/domains/mobile-web/utils/price';
import Button from '@/shared/components/ui/Button';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import Pagination from '@/shared/components/ui/Pagination/Pagination';
import { useColorStore } from '@/store/colorStore';

const PAGE_SIZE = 10;
const SEARCH_DELAY_MS = 300;

const ProductManagement = () => {
  const navigate = useNavigate();
  const { NEWCOLOR } = useColorStore();
  const [currentPage, setCurrentPage] = useState(1);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  // The list is asked for once typing pauses; a new keyword starts at page 1
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setCurrentPage(1);
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: summary } = useAdminProductSummary();
  const { data: page, isLoading } = useAdminProducts({
    page: currentPage,
    size: PAGE_SIZE,
    status,
    q: query,
  });

  const rows = page?.rows ?? [];
  const totalPages = Math.max(1, page?.total_pages ?? 1);

  return (
    <section className="flex! flex-col gap-6 overflow-auto mb-10">
      <h1 className="capitalize title2medium">Pengaturan Produk</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 md:gap-6 gap-3">
        <CardTotal title="total produk" total={summary?.total_products ?? '-'} unit="buku" />
        <CardTotal title="Total Stok" total={summary?.total_stock ?? '-'} unit="Unit" />
        <CardTotal title="Jumlah Stok Kosong" total={summary?.out_of_stock ?? '-'} unit="Produk" />
        <CardTotal title="Jumlah Stok Sedikit" total={summary?.low_stock ?? '-'} unit="Produk" />
      </div>

      <div className="shadowCard flex flex-col gap-3">
        <div className="w-full p-3 md:p-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="shrink-0">
            <p className="title1bold capitalize">Daftar Produk</p>
            <p className="body1 text-text-grey">{page?.total ?? 0} produk ditemukan</p>
          </div>
          <div className="flex flex-col   gap-3 w-full sm:flex-row md:flex-1 md:justify-end md:max-w-[680px]">
            <DetailForm
              inputType="select"
              className="w-full sm:w-[180px] sm:shrink-0"
              showTitle={false}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setCurrentPage(1);
              }}
              optionSelect={[{ value: '', label: 'Semua Status' }, ...PRODUCT_STATUS_OPTIONS]}
              disableEmptyOption={false}
            />
            <DetailForm
              inputType="text"
              placeholder="ID Produk, Nama produk atau Kategori"
              showTitle={false}
              inputFormStyle="store"
              className="w-full sm:flex-1 sm:min-w-0"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button
              onClick={() => navigate('/dashboard/product-detail')}
              customButton="btn-fill-blue rounded-[12px]! "
            >
              <Plus />
              <span className="">Tambah</span>
            </Button>
          </div>
        </div>
        <div className=" divider h-[0.5px] w-full bg-blue-cs-200" />
        <div className="flex-col flex gap-6 p-3 md:p-6 w-full overflow-auto min-h-[200px] max-h-[500px]">
          {rows.length > 0 ? (
            rows.map((product) => {
              const badge = STATUS_COURIER[PRODUCT_STATUS_BADGE[product.availability]];
              return (
                <CardRow
                  key={product.id}
                  code={product.sku}
                  badge={{
                    label: product.availability_label ?? badge.label,
                    bg: NEWCOLOR[badge.bg],
                    color: badge.color,
                  }}
                  subtitle={product.title}
                  fields={[
                    { key: 'category', label: 'Kategori', value: product.category?.name ?? '-' },
                    { key: 'author', label: 'Penulis', value: product.author ?? '-' },
                    { key: 'isbn', label: 'Nomor ISBN', value: product.isbn ?? '-' },
                  ]}
                  summaries={[
                    {
                      key: 'stock',
                      label: 'Jumlah Stok Tersedia',
                      value: product.available_stock,
                      valueClassName: 'text-black',
                    },
                    {
                      key: 'price',
                      label: 'Harga Akhir',
                      value: `Rp ${formatPrice(Number(product.final_price))}`,
                    },
                  ]}
                  actions={[
                    {
                      key: 'detail',
                      label: 'Detail',
                      variant: 'outline',
                      onClick: () =>
                        navigate(`/dashboard/product-detail?id=${product.id}`, {
                          state: { code: product.sku },
                        }),
                    },
                  ]}
                />
              );
            })
          ) : (
            <p className="text-center">{isLoading ? 'Memuat...' : 'Tidak ada data'}</p>
          )}
        </div>

        <div className="divider h-[0.5px] w-full bg-blue-cs-200" />
        <div className="w-full p-3 md:p-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="body1 text-text-grey whitespace-nowrap">
            Menampilkan <span className="font-bold text-text-black">{PAGE_SIZE}</span> Data per
            Halaman
          </p>
          <div className="flex  items-center gap-[6px] justify-center m-auto lg:m-0">
            <Pagination
              pageCount={totalPages}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              previousLabel={<ChevronLeft size={16} />}
              nextLabel={<ChevronRight size={16} />}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductManagement;
